import React, { useEffect, useState, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { transactionsApi } from '../utils/api';
import { formatRupee, formatDate, formatQty } from '../utils/format';
import EditTransactionModal from '../components/EditTransactionModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

function StockLedgerBlock({ stock, onTransactionChange }) {
  const [open, setOpen] = useState(false);
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadTxns = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await transactionsApi.getByStock(stock._id, { sortBy: 'purchaseDate', order: 'desc' });
      setTxns(res.data.data);
      setSummary(res.data.summary);
    } catch (e) {
      console.error('Failed to load transactions', e);
    } finally {
      setLoading(false);
    }
  }, [open, stock._id]);

  useEffect(() => { loadTxns(); }, [loadTxns]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await transactionsApi.delete(deleteTarget._id);
      setDeleteTarget(null);
      await loadTxns();
      onTransactionChange();
    } catch (e) {
      alert(e.response?.data?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditSuccess = async () => {
    setEditTarget(null);
    await loadTxns();
    onTransactionChange();
  };

  return (
    <div className="ledger-block">
      {/* Header */}
      <div className="ledger-header" onClick={() => setOpen(o => !o)}>
        <div className="stock-name-cell">
          <div className="stock-avatar">{stock.stockName.slice(0, 3)}</div>
          <div className="ledger-header-info">
            <strong style={{ fontSize: '0.95rem', fontWeight: 700 }}>{stock.stockName}</strong>
            {stock.ticker && <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{stock.ticker}</small>}
          </div>
        </div>

        <div className="ledger-header-stats">
          <div className="ledger-stat">
            <div className="ls-label">Qty</div>
            <div className="ls-value">{formatQty(stock.totalQty)}</div>
          </div>
          <div className="ledger-stat">
            <div className="ls-label">Avg Price</div>
            <div className="ls-value">{formatRupee(stock.avgBuyPrice)}</div>
          </div>
          <div className="ledger-stat">
            <div className="ls-label">Total Invested</div>
            <div className="ls-value" style={{ color: 'var(--accent)' }}>{formatRupee(stock.totalInvestment)}</div>
          </div>
          <div className="ledger-stat">
            <div className="ls-label">Entries</div>
            <div className="ls-value">
              <span className="badge badge-neutral">{stock.transactionCount}</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 20px', flexShrink: 0 }}>
            <span className={`chevron ${open ? 'open' : ''}`}>▼</span>
          </div>
        </div>
      </div>

      {/* Body */}
      {open && (
        <div className="ledger-body">
          {/* Summary bar */}
          {summary && (
            <div style={{
              display: 'flex', gap: 24, padding: '12px 20px',
              background: 'var(--bg3)', borderBottom: '1px solid var(--border)',
              flexWrap: 'wrap', fontSize: '0.8rem'
            }}>
              <span>Total Buy Qty: <strong className="mono">{formatQty(summary.totalBuyQty)}</strong></span>
              {summary.totalSellQty > 0 && (
                <span>Total Sell Qty: <strong className="mono text-red">{formatQty(summary.totalSellQty)}</strong></span>
              )}
              <span>Net Qty: <strong className="mono">{formatQty(summary.totalQty)}</strong></span>
              <span>Avg Price: <strong className="mono">{formatRupee(summary.avgBuyPrice)}</strong></span>
              <span>Total Invested: <strong className="mono text-accent">{formatRupee(summary.totalInvestment)}</strong></span>
            </div>
          )}

          {loading ? (
            <div className="loading-wrap"><div className="spinner" /></div>
          ) : txns.length === 0 ? (
            <div className="empty-state" style={{ padding: 32 }}>
              <p>No transactions yet for this stock.</p>
            </div>
          ) : (
            <table style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '130px' }} />
                <col style={{ width: '90px' }} />
                <col style={{ width: '110px' }} />
                <col style={{ width: '155px' }} />
                <col style={{ width: '155px' }} />
                <col style={{ width: 'auto' }} />
                <col style={{ width: '140px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'right' }}>Quantity</th>
                  <th style={{ textAlign: 'right' }}>Price / Share</th>
                  <th style={{ textAlign: 'right' }}>Total Value</th>
                  <th>Notes</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {txns.map(t => (
                  <tr key={t._id}>
                    <td className="mono" style={{ fontSize: '0.83rem', whiteSpace: 'nowrap' }}>{formatDate(t.purchaseDate)}</td>
                    <td>
                      <span className={`badge ${t.type === 'BUY' ? 'badge-buy' : 'badge-sell'}`}>
                        {t.type}
                      </span>
                    </td>
                    <td className="mono" style={{ textAlign: 'right' }}>{formatQty(t.quantity)}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{formatRupee(t.price)}</td>
                    <td className="mono text-accent" style={{ textAlign: 'right', fontWeight: 600 }}>{formatRupee(t.quantity * t.price)}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.83rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.notes || <span style={{ color: 'var(--text-dim)' }}>—</span>}
                    </td>
                    <td>
                      <div className="action-btns" style={{ justifyContent: 'center' }}>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={(e) => { e.stopPropagation(); setEditTarget(t); }}
                        >✎ Edit</button>
                        <button
                          className="btn btn-danger btn-sm btn-icon"
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(t); }}
                        >✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {editTarget && (
        <EditTransactionModal
          transaction={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={handleEditSuccess}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          title="Delete Transaction?"
          message={`Delete the ${deleteTarget.type} of ${formatQty(deleteTarget.quantity)} shares at ${formatRupee(deleteTarget.price)} on ${formatDate(deleteTarget.purchaseDate)}?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  );
}

export default function Ledger({ navigate }) {
  const { stocks, loading, error, fetchStocks } = useApp();
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    fetchStocks({ search });
  }, [fetchStocks, search]);

  useEffect(() => { load(); }, [load]);

  const filtered = stocks.filter(s =>
    s.stockName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="page-header">
        <h2>Stock Ledger</h2>
        <p>All transactions grouped by stock — click a stock to expand</p>
      </div>

      <div className="toolbar">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input
            type="text"
            placeholder="Filter stocks…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button className="btn btn-primary" onClick={() => navigate('add')}>
          + Add Transaction
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading-wrap"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No stocks found</h3>
          <p>Add transactions to start building your ledger.</p>
        </div>
      ) : (
        filtered.map(stock => (
          <StockLedgerBlock
            key={stock._id}
            stock={stock}
            onTransactionChange={load}
          />
        ))
      )}
    </div>
  );
}